// Platform layer. Game code never touches browser APIs directly; it goes
// through this interface so a desktop (Electron/Tauri) build can swap the
// implementation. See AGENTS.md "Platform layer".
export interface Platform {
  loadSetting(key: string): string | null;
  saveSetting(key: string, value: string): void;
  // True when the primary pointer is a finger (phone/tablet): no hover, no
  // precise pointer. Drives the on-screen touch controls.
  isTouchPrimary(): boolean;
  // Reload the whole client (after a server update or protocol mismatch).
  reload(): void;
  // Called when the page becomes visible again or the network comes back
  // (laptop lid, phone lock screen, Wi-Fi change): time to check the link.
  onWake(cb: () => void): void;
  // Room id from the page URL (?room=abc123), and writing it back after
  // the server tells us which room we are in, so the address bar is the
  // invite link.
  getRoomFromUrl(): string | null;
  getQueryParam(name: string): string | null;
  setRoomInUrl(id: string): void;
  inviteLink(id: string): string;
  copyText(text: string): Promise<boolean>;
}

const PREFIX = 'mayday.';

const webPlatform: Platform = {
  loadSetting(key) {
    try {
      return localStorage.getItem(PREFIX + key);
    } catch {
      return null;
    }
  },
  saveSetting(key, value) {
    try {
      localStorage.setItem(PREFIX + key, value);
    } catch {
      // Private mode / blocked storage: the setting just won't persist.
    }
  },
  isTouchPrimary() {
    try {
      const coarse = matchMedia('(pointer: coarse)').matches;
      const noHover = matchMedia('(hover: none)').matches;
      const anyFine = matchMedia('(any-pointer: fine)').matches;
      // A laptop with a touchscreen still has a fine pointer: keep mouse mode.
      return coarse && noHover && !anyFine && navigator.maxTouchPoints > 0;
    } catch {
      return false;
    }
  },
  reload() {
    location.reload();
  },
  getQueryParam(name) {
    try {
      return new URLSearchParams(location.search).get(name);
    } catch {
      return null;
    }
  },
  getRoomFromUrl() {
    try {
      const v = new URLSearchParams(location.search).get('room');
      return v && /^[a-z0-9]{1,16}$/i.test(v) ? v : null;
    } catch {
      return null;
    }
  },
  setRoomInUrl(id) {
    try {
      const url = new URL(location.href);
      if (url.searchParams.get('room') === id && !url.searchParams.has('mode')) return;
      url.searchParams.set('room', id);
      url.searchParams.delete('mode'); // a reload should return to this room, not re-roll quick play
      history.replaceState(null, '', url.toString());
    } catch {
      /* not fatal */
    }
  },
  inviteLink(id) {
    const url = new URL(location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('room', id);
    return url.toString();
  },
  async copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },
  onWake(cb) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') cb();
    });
    window.addEventListener('online', cb);
    window.addEventListener('pageshow', cb);
  },
};

export const platform: Platform = webPlatform;

// Phaser rasterises Text into a 1x texture by default, which the canvas then
// upscales on high-DPI screens (blurry). Pass this as the text style's
// `resolution` so glyphs are drawn at native pixel density. Capped: a 3x
// texture is plenty and keeps memory sane on phones.
export const TEXT_RESOLUTION: number = Math.min(3, Math.max(1, typeof devicePixelRatio === 'number' ? devicePixelRatio : 1));
